import React, {useCallback, useEffect, useState} from 'react';
import {
  Button,
  Dropdown,
  Dialogue,
  DropdownItem,
  Icon,
  Modal,
  Text,
  TextField
} from '@albert-io/atomic';
import {convertToFolder} from './ConvertToFolder.utils';
import {Link} from 'react-router';
import {TemplateModelV1} from 'resources/GeneratedModels/Template/TemplateModel.v1';

interface FolderDropdownProps {
  onClick: () => void;
}

export const FolderOptionsDropdown = ({onClick}: FolderDropdownProps) => {
  return (
    <Dropdown
      wrapperClassName=' u-mar-r_1'
      trigger={
        <Button variant='outlined' color='secondary'>
          Options
          <Icon className='u-mar-l_1' icon='caret-down' />
        </Button>
      }
    >
      <DropdownItem
        icon={['far', 'file-export']}
        style={{
          width: '250px'
        }}
        subtext='Questions in this assignment will be moved to a folder.'
        subtextProps={{emphasis: 'medium'}}
        onClick={onClick}
      >
        Convert To Folder
      </DropdownItem>
    </Dropdown>
  );
};

interface FolderModalProps {
  assignmentName: string;
  questionsList: any;
  handleClose: () => void;
  onFolderCreation?: (folder) => void;
  onDismiss?: () => void;
}

export const FolderModal = ({
  handleClose,
  assignmentName,
  questionsList,
  onFolderCreation,
  onDismiss
}: FolderModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [folderName, setFolderName] = useState(assignmentName);
  const [folderNameError, setFolderNameError] = useState(false);
  const [newlyCreatedFolder, setNewlyCreatedFolder] = useState<TemplateModelV1 | null>(null);

  const handleConvertToFolder = useCallback(async () => {
    if (!folderName.length) {
      setFolderNameError(true);
      return;
    }
    setIsLoading(true);
    const newFolder = await convertToFolder(folderName, questionsList);
    setNewlyCreatedFolder(newFolder);
    onFolderCreation?.(newFolder);
    setIsDone(true);
    setIsLoading(false);
  }, [questionsList, folderName]);

  useEffect(() => {
    // skip asking for name if we have
    if (assignmentName.length > 0) {
      handleConvertToFolder();
    }
  }, [assignmentName, convertToFolder]);

  /**
   * Render header of modal
   */
  const renderHeader = useCallback(() => {
    if (isLoading) {
      return <Icon icon='spinner' spin className='fa-lg u-mar-b_2 u-mar-t_1' />;
    }
    if (isDone && newlyCreatedFolder === null) {
      return (
        <Icon
          color='danger'
          icon={['fal', 'triangle-exclamation']}
          className='fa-lg u-mar-b_2 u-mar-t_1'
        />
      );
    }
    if (isDone) {
      return <Icon icon='check' className='fa-lg u-mar-b_2 u-mar-t_1' />;
    }
    return <Icon icon={['fal', 'folder-open']} className='fa-lg u-mar-b_2 u-mar-t_1' />;
  }, [isLoading, isDone, newlyCreatedFolder]);
  /**
   * Render body of modal
   */
  const renderBody = useCallback(() => {
    if (isLoading) {
      return <Text bold>Moving questions...</Text>;
    }
    if (isDone && newlyCreatedFolder === null) {
      return (
        <>
          <Text as='p' bold>
            Error
          </Text>
          <Text as='p'>We were unable to move your questions to a folder</Text>
        </>
      );
    }
    if (isDone) {
      return (
        <>
          <Text as='p' bold>
            Done!
          </Text>
          <Text as='p' className='u-mar-b_0'>
            Questions successfully moved to your Folders!
          </Text>
        </>
      );
    }
    return (
      <TextField
        size='l'
        label='Name your Folder'
        value={folderName}
        placeholder='Untitled'
        onChange={(e) => {
          if (e.target.value) {
            setFolderNameError(false);
          }
          setFolderName((e.target as HTMLInputElement).value);
        }}
        error={folderNameError}
        message={folderNameError && 'Please provide a folder name'}
      />
    );
  }, [isLoading, isDone, setFolderName, folderName, setFolderNameError, folderNameError]);
  /**
   * Render button group of modal
   */
  const renderBtnGroup = useCallback(() => {
    if (isLoading) {
      return (
        <>
          <Button variant='outlined' color='secondary' className='u-mar-b_3' onClick={handleClose}>
            Cancel
          </Button>
          <Text className='u-text-align_center' emphasis='lowest'>
            Note: Folders will not open in Assignment Builder.
          </Text>
        </>
      );
    }

    if (isDone && newlyCreatedFolder === null) {
      return (
        <>
          <Button
            variant='outlined'
            className='u-mar-b_1'
            onClick={() => {
              setIsDone(false);
              handleConvertToFolder();
            }}
          >
            Try again
          </Button>
          <Button color='destructive' variant='outlined' onClick={handleClose}>
            Cancel
          </Button>
        </>
      );
    }

    if (isDone && newlyCreatedFolder) {
      return (
        <>
          <Button
            variant='outlined'
            color='secondary'
            className='u-mar-b_1'
            onClick={() => {
              onDismiss?.();
              handleClose();
            }}
          >
            Dismiss
          </Button>
          <Button as={Link} to={`/folder/${newlyCreatedFolder.getId()}`}>
            View Folder
          </Button>
        </>
      );
    }

    return (
      <>
        <Button variant='outlined' color='secondary' className='u-mar-b_1' onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='outlined' onClick={handleConvertToFolder}>
          Convert to Folder
        </Button>
      </>
    );
  }, [isLoading, isDone, handleClose, folderName, onDismiss, newlyCreatedFolder, setIsDone]);

  return (
    <Modal ariaLabel='Convert to Folder' handleClose={handleClose} dismissable={false}>
      {({modalContentStyle}) => {
        return (
          <Dialogue inModal className={modalContentStyle} hideCloseBtn size='s'>
            <Dialogue.Title align='center'>{renderHeader()}</Dialogue.Title>
            <Dialogue.Body align='center'>{renderBody()}</Dialogue.Body>
            <Dialogue.BtnGroup direction='column' fillColumn>
              {renderBtnGroup()}
            </Dialogue.BtnGroup>
          </Dialogue>
        );
      }}
    </Modal>
  );
};
